import { useCallback } from "react"
import { DESC } from "helpers/sorts"
import { useFactorViewDashboardMetrics } from "helpers/metrics/hooks"
import { getPerformanceFilterInstrumentColumns } from "helpers/metrics"
import { getPerformanceApiStartDate } from "helpers/dates"
import { INSTRUMENTS } from "connected-components/dashboard/helpers"
import { useTemplateGatherers } from "connected-components/dashboard/hooks/use-template-gatherers"
import ContributorsDataIntegrator from "gatherers/contributors-data-integrator"
import { useNormalPerformancePricingRefreshWarning } from "gatherers/hooks/use-normal-performance-pricing-refresh-warning"
import ContributorsTable from "renderers/contributors-datagrid"
import { useAnalytics } from "connected-components/analytics"
import { useFactorsViewData } from "connected-components/factors-view/hooks/use-factors-view-data"
import { usePanelId } from "connected-components/dashboard/context/panel-id"
import { useDrilldownOpener } from "connected-components/dashboard/drilldown/use-drilldown-opener"
import { buildDrilldownIntent } from "connected-components/dashboard/drilldown/build-drilldown-intent"
import { resolveIntradayDrilldownFields } from "connected-components/dashboard/drilldown/resolve-intraday-drilldown-fields"
import { useFactorsViewTemplateChart } from "../../hooks/use-factors-view-template-chart"
import { FACTORS_VIEW_PERFORMANCE_TEMPLATE_KEY, FACTORS_VIEW_PERFORMANCE_DEFAULTS } from "../template-chart-config"
import { useFactorsViewTableHandlers } from "../../hooks/use-factors-view-table-handlers"
import { FACTORS_VIEW_COMMON_PROPS } from "../../prop-types"

export default function FactorsPerformanceView({ instrumentsWithMetadata, instrumentColumns }) {
  const { selectedMetricDisplay } = useFactorsViewTemplateChart(
    FACTORS_VIEW_PERFORMANCE_TEMPLATE_KEY,
    FACTORS_VIEW_PERFORMANCE_DEFAULTS,
  )
  const contextData = useFactorsViewData()
  const { base: contextBase, sortMetricId, setSortMetricId, sortDirection, setSortDirection, selectedTableLevels, setSelectedTableLevels, period, latestDate, performanceEndDate, performanceStartDate, isEndVsStartDateComparison, startDate, endDate, instrument, comparisonInstrument, secondInstrument, filter, normalization, modelId, intradayBatchMetadata, intradayPricingAsOf, isIntradayMode } = contextData

  const analytics = useAnalytics()
  const { handleSortChanged, handleTableNavigationChanged } = useFactorsViewTableHandlers({ track: analytics.track })
  const panelId = usePanelId()
  const openDrilldown = useDrilldownOpener()
  const apiStartDate = getPerformanceApiStartDate({ period, isIntradayMode, latestDate, performanceStartDate, performanceEndDate })

  const handleDrilldownRowClick = useCallback((factorId, clickPosition) => {
    if (!panelId) return
    openDrilldown(buildDrilldownIntent({
      panelId,
      sourceSurface: "factors.performance",
      title: "Asset View: Performance",
      view: "performance",
      context: {
        modelId,
        instrument,
        comparisonInstrument,
        secondInstrument,
        base: contextBase,
        startDate: apiStartDate,
        endDate: performanceEndDate,
        isIntradayMode,
        ...resolveIntradayDrilldownFields(isIntradayMode, intradayBatchMetadata, intradayPricingAsOf),
        filter,
        normalization,
        isEndVsStartDateComparison,
      },
      clickPosition,
      seed: { selectedFactor: factorId, selectedMetricDisplay },
    }))
  }, [panelId, openDrilldown, modelId, instrument, comparisonInstrument, secondInstrument, contextBase, apiStartDate, performanceEndDate, isIntradayMode, intradayBatchMetadata, intradayPricingAsOf, filter, normalization, isEndVsStartDateComparison, selectedMetricDisplay])

  const gatherers = useTemplateGatherers(contextData, "factorPerformance", {
    from: apiStartDate,
    to: performanceEndDate,
    selectedMetricDisplay,
    useCompositionQueries: false,
  })

  useNormalPerformancePricingRefreshWarning({ gatherers, startDate, endDate })

  const metrics = useFactorViewDashboardMetrics(
    [
      {
        category: "factorPerformance",
        selectedMetricDisplay,
        period,
        filterInstrumentColumns: getPerformanceFilterInstrumentColumns(isEndVsStartDateComparison),
      },
    ],
    { instrumentColumns, isActive: !!contextBase, instrumentsWithMetadata, isEndVsStartDateComparison },
  )

  const defaultSortSuffix = INSTRUMENTS[instrumentColumns[0]].suffix

  return <ContributorsDataIntegrator
    modelId={modelId}
    mainInstrument={instrument}
    gatherers={gatherers}
    metrics={metrics}
    Renderer={ContributorsTable}
    rendererProps={{
      groupByName: "Category",
      defaultSort: {
        path: `cumulativeReturn${defaultSortSuffix}`,
        direction: DESC,
      },
      disableCategories: true,
      classes: "factors-table",
      idLabel: "Factor",
      hidePagination: true,
      includeDrilldownArrows: true,
      downloadKey: "factors.performance",
      downloadName: "Factors - Performance",
      sortMetricId,
      onSortMetricIdChange: setSortMetricId,
      sortDirection,
      onSortDirectionChange: setSortDirection,
      tableNavigationLevels: selectedTableLevels,
      onTableNavigationChange: setSelectedTableLevels,
      onSortChanged: handleSortChanged,
      onTableNavigationChanged: handleTableNavigationChanged,
      onDrilldownRowClick: handleDrilldownRowClick,
      viewLabel: "Performance",
    }}
  />
}

FactorsPerformanceView.propTypes = FACTORS_VIEW_COMMON_PROPS
